// src/pages/wishlist.js
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  fetchWishlist, 
  clearWishlist, 
  moveToCart 
} from '../store/slices/wishlistSlice';
import { setError, setSuccess } from '../store/slices/uiSlice';
import Loader from '../components/common/loader';
import WishlistButton from '../components/wishlist/wishlistButton';

const Wishlist = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector(state => state.auth);
  const { wishlist, itemsCount, loading } = useSelector(state => state.wishlist);
  
  // Fetch wishlist on component mount
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=wishlist');
      return;
    }
    
    dispatch(fetchWishlist());
  }, [dispatch, isAuthenticated, navigate]);
  
  // Handle clear wishlist
  const handleClearWishlist = () => {
    if (window.confirm('Are you sure you want to clear your wishlist?')) {
      dispatch(clearWishlist())
        .unwrap()
        .then(() => {
          dispatch(setSuccess('Wishlist cleared successfully'));
        })
        .catch((err) => {
          dispatch(setError(err.message || 'Failed to clear wishlist'));
        });
    }
  };
  
  // Handle move to cart
  const handleMoveToCart = (productId) => {
    dispatch(moveToCart(productId))
      .unwrap()
      .then(() => {
        dispatch(setSuccess('Item moved to cart successfully'));
      })
      .catch((err) => {
        dispatch(setError(err.message || 'Failed to move item to cart'));
      });
  };
  
  // Calculate discount percentage
  const calculateDiscount = (mrp, price) => {
    if (!mrp || !price || mrp <= price) return 0;
    return Math.round(((mrp - price) / mrp) * 100);
  };
  
  // Format name helper
  const formatName = (name) => {
    return name?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
  };
  
  if (loading.wishlist) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center">
        <Loader size="lg" />
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">My Wishlist</h1>
        <p className="text-gray-600">
          Save your favorite agriculture and packaging products
        </p>
      </div>
      
      {itemsCount === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
          
          <h2 className="text-xl font-semibold mb-2">Your Wishlist is Empty</h2>
          <p className="text-gray-600 mb-6">
            Save items you love in your wishlist and revisit them anytime.
          </p>
          
          <Link 
            to="/products" 
            className="inline-block bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition"
          >
            Start Shopping
          </Link>
        </div>
      ) : (
        <div>
          {/* Header with stats and actions */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="flex justify-between items-center flex-wrap gap-4">
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-sm text-gray-500">Total Items</p>
                  <p className="text-2xl font-bold text-gray-900">{itemsCount}</p>
                </div>
                
                {wishlist.totalValue && (
                  <>
                    <div className="border-l pl-6">
                      <p className="text-sm text-gray-500">Total Value</p>
                      <p className="text-2xl font-bold text-green-600">₹{wishlist.totalValue.toFixed(2)}</p>
                    </div>
                    
                    {wishlist.totalSavings > 0 && (
                      <div className="border-l pl-6">
                        <p className="text-sm text-gray-500">Total Savings</p>
                        <p className="text-2xl font-bold text-green-600">₹{wishlist.totalSavings.toFixed(2)}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              <button
                onClick={handleClearWishlist}
                className="text-red-600 hover:text-red-700 font-medium"
                disabled={loading.clear}
              >
                {loading.clear ? 'Clearing...' : 'Clear Wishlist'}
              </button>
            </div>
          </div>
          
          {/* Products grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {wishlist.items.map(item => (
              <div key={item.productId} className="bg-white rounded-lg shadow overflow-hidden hover:shadow-lg transition">
                <div className="relative">
                  <Link to={`/product/${item.productId}`}>
                    <div className="h-48 overflow-hidden bg-gray-100">
                      <img 
                        src={item.currentImageUrl || item.imageUrl || '/images/placeholder.png'} 
                        alt={item.name} 
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  </Link>
                  
                  {/* Stock Badge */}
                  {item.stockStatus === 'LOW_STOCK' && (
                    <span className="absolute top-2 left-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded font-medium">
                      Low Stock
                    </span>
                  )}
                  {item.stockStatus === 'OUT_OF_STOCK' && (
                    <span className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded font-medium">
                      Out of Stock
                    </span>
                  )}
                  
                  {/* Eco-Friendly Badge */}
                  {item.isEcoFriendly && (
                    <span className="absolute top-2 right-10 bg-green-500 text-white text-xs px-2 py-1 rounded font-medium">
                      🌱 Eco
                    </span>
                  )}
                  
                  {/* Wishlist Button */}
                  <div className="absolute top-2 right-2">
                    <WishlistButton productId={item.productId} />
                  </div>
                </div>
                
                <Link to={`/product/${item.productId}`}>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-1 truncate" title={item.name}>
                      {item.name}
                    </h3>
                    
                    {/* Brand/Manufacturer */}
                    <div className="text-xs text-gray-500 mb-2">
                      {item.brand && <p>Brand: {item.brand}</p>}
                      {item.manufacturer && <p>By {item.manufacturer}</p>}
                    </div>
                    
                    {/* Category/Material/Color Tags */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        {formatName(item.subCategory || item.category)}
                      </span>
                      {item.material && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          {formatName(item.material)}
                        </span>
                      )}
                      {item.color && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                          {formatName(item.color)}
                        </span>
                      )}
                    </div>
                    
                    {/* Specifications */}
                    {item.specifications && (
                      <div className="text-xs text-gray-600 mb-2 space-y-1">
                        {item.specifications.thickness?.value && (
                          <p>Thickness: {item.specifications.thickness.value} {item.specifications.thickness.unit}</p>
                        )}
                        {item.specifications.gsm && (
                          <p>GSM: {item.specifications.gsm}</p>
                        )}
                        {item.specifications.weight?.value && (
                          <p>Weight: {item.specifications.weight.value} {item.specifications.weight.unit}</p>
                        )}
                      </div>
                    )}
                    
                    {/* Price */}
                    <div className="flex items-end mb-2">
                      <span className="text-lg font-bold text-gray-900">
                        ₹{item.currentPrice || item.price}
                      </span>
                      
                      {(item.currentMrp || item.mrp) > (item.currentPrice || item.price) && (
                        <>
                          <span className="text-sm text-gray-500 line-through ml-2">
                            ₹{item.currentMrp || item.mrp}
                          </span>
                          
                          <span className="text-sm text-green-600 ml-2">
                            {calculateDiscount(item.currentMrp || item.mrp, item.currentPrice || item.price)}% off
                          </span>
                        </>
                      )}
                    </div>
                    
                    {/* Price Alert */}
                    {item.priceChanged && (
                      <div className={`text-xs mb-2 ${item.priceDecrease ? 'text-green-600' : 'text-red-600'}`}>
                        {item.priceDecrease ? '📉' : '📈'} Price {item.priceDecrease ? 'decreased' : 'increased'} by ₹{Math.abs(item.priceDifference)}
                      </div>
                    )}
                    
                    {/* Bulk Pricing Indicator */}
                    {item.hasBulkPricing && (
                      <p className="text-xs text-blue-600 mb-2">
                        💰 Bulk pricing from {item.bulkStartsFrom}+ units
                      </p>
                    )}
                    
                    {/* Min Order Quantity */}
                    {item.minOrderQuantity > 1 && (
                      <p className="text-xs text-gray-500 mb-2">
                        Min Order: {item.minOrderQuantity} units
                      </p>
                    )}
                    
                    {/* User Notes */}
                    {item.notes && (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                        <p className="text-gray-700">📝 {item.notes}</p>
                      </div>
                    )}
                  </div>
                </Link>
                
                {/* Action Buttons */}
                <div className="px-4 pb-4 space-y-2">
                  <button
                    onClick={() => handleMoveToCart(item.productId)}
                    className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition flex items-center justify-center disabled:bg-gray-400 disabled:cursor-not-allowed"
                    disabled={loading.moveToCart || item.stockStatus === 'OUT_OF_STOCK'}
                  >
                    {loading.moveToCart && item.productId === loading.moveProductId ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Moving...
                      </>
                    ) : item.stockStatus === 'OUT_OF_STOCK' ? (
                      'Out of Stock'
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        Move to Cart
                      </>
                    )}
                  </button>
                  
                  <Link
                    to={`/product/${item.productId}`}
                    className="block w-full bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition text-center"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
          
          {/* Analytics Section */}
          {wishlist.analytics && (
            <div className="mt-8 bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold mb-4">Wishlist Analytics</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Category Breakdown */}
                {wishlist.analytics.categories && Object.keys(wishlist.analytics.categories).length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2 text-gray-700">Categories</h3>
                    <div className="space-y-2">
                      {Object.entries(wishlist.analytics.categories).map(([category, count]) => (
                        <div key={category} className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">{formatName(category)}</span>
                          <span className="text-sm font-medium">{count} {count === 1 ? 'item' : 'items'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Material Breakdown */}
                {wishlist.analytics.materials && Object.keys(wishlist.analytics.materials).length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2 text-gray-700">Materials</h3>
                    <div className="space-y-2">
                      {Object.entries(wishlist.analytics.materials).map(([material, count]) => (
                        <div key={material} className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">{formatName(material)}</span>
                          <span className="text-sm font-medium">{count} {count === 1 ? 'item' : 'items'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Price Range Breakdown */}
                {wishlist.analytics.priceRanges && (
                  <div>
                    <h3 className="font-semibold mb-2 text-gray-700">Price Ranges</h3>
                    <div className="space-y-2">
                      {wishlist.analytics.priceRanges.under500 > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Under ₹500</span>
                          <span className="text-sm font-medium">{wishlist.analytics.priceRanges.under500}</span>
                        </div>
                      )}
                      {wishlist.analytics.priceRanges['500to2000'] > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">₹500 - ₹2,000</span>
                          <span className="text-sm font-medium">{wishlist.analytics.priceRanges['500to2000']}</span>
                        </div>
                      )}
                      {wishlist.analytics.priceRanges['2000to5000'] > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">₹2,000 - ₹5,000</span>
                          <span className="text-sm font-medium">{wishlist.analytics.priceRanges['2000to5000']}</span>
                        </div>
                      )}
                      {wishlist.analytics.priceRanges.above5000 > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Above ₹5,000</span>
                          <span className="text-sm font-medium">{wishlist.analytics.priceRanges.above5000}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Flags Summary */}
              {wishlist.analytics.flags && (
                <div className="mt-6 pt-6 border-t">
                  <div className="flex flex-wrap gap-4">
                    {wishlist.analytics.flags.ecoFriendlyCount > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-green-600">🌱</span>
                        <span className="text-sm">{wishlist.analytics.flags.ecoFriendlyCount} Eco-Friendly</span>
                      </div>
                    )}
                    {wishlist.analytics.flags.bulkPricingAvailable > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-blue-600">💰</span>
                        <span className="text-sm">{wishlist.analytics.flags.bulkPricingAvailable} with Bulk Pricing</span>
                      </div>
                    )}
                    {wishlist.analytics.flags.outOfStockCount > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-red-600">⚠️</span>
                        <span className="text-sm">{wishlist.analytics.flags.outOfStockCount} Out of Stock</span>
                      </div>
                    )}
                    {wishlist.analytics.flags.priceDecreasedCount > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-green-600">📉</span>
                        <span className="text-sm">{wishlist.analytics.flags.priceDecreasedCount} Price Decreased</span>
                      </div>
                    )}
                    {wishlist.analytics.flags.priceIncreasedCount > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-red-600">📈</span>
                        <span className="text-sm">{wishlist.analytics.flags.priceIncreasedCount} Price Increased</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Wishlist;